'use client';
import { useSize } from 'ahooks';
import { type FC, useRef } from 'react';
import type { IndividualPlanType } from '../constants';

/** 刻度块宽度（px） */
const TICK_WIDTH = 6;
/** 刻度块间距（px，等同 Tailwind gap-1） */
const TICK_GAP = 4;
/** 容器宽度未测到时的默认刻度数，避免首帧空白 */
const TICK_FALLBACK = 24;
/** 单条进度条最多容纳的刻度数，用于稳定 key 池 */
const TICK_MAX = 80;
const TICK_KEYS = Array.from({ length: TICK_MAX }, (_, i) => `tick-${i}`);

/** 各 plan 的进度条整体渐变（每块按位置切片，呈现整条左→右渐变） */
const TICK_GRADIENT: Record<IndividualPlanType, string> = {
  free: '',
  standard_v2: 'linear-gradient(91deg, #CFE7FF 3.96%, #79A4FF 96.78%)',
  premium_v2: 'linear-gradient(91deg, #FAF1C8 3.96%, #BDA16C 96.78%)',
  ultimate_v2: 'linear-gradient(90deg, #5765F2 0%, #BA93FF 91.53%)',
};

interface CreditsProgressBarProps {
  /** 个人版按 planType 自动取主题渐变；企业版改走 `gradient` 直接传。两者择一即可。 */
  planType?: IndividualPlanType;
  /** 自定义渐变（优先级高于 planType 的查表结果） */
  gradient?: string;
  /** 0-100 的整数百分比 */
  progress: number;
  className?: string;
}

export const CreditsProgressBar: FC<CreditsProgressBarProps> = ({ planType, gradient, progress, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const size = useSize(containerRef);
  const width = size?.width ?? 0;

  const tickCount =
    width > 0
      ? Math.min(TICK_MAX, Math.max(1, Math.floor((width + TICK_GAP) / (TICK_WIDTH + TICK_GAP))))
      : TICK_FALLBACK;
  const activeCount = Math.round((progress / 100) * tickCount);
  const resolvedGradient = gradient ?? (planType ? TICK_GRADIENT[planType] : '');

  return (
    <div
      ref={containerRef}
      aria-hidden
      className={`flex h-3 flex-1 items-center gap-1 overflow-hidden ${className ?? ''}`}
    >
      {TICK_KEYS.slice(0, tickCount).map((key, i) => {
        const isActive = i < activeCount;
        return (
          <div
            key={key}
            className="h-full shrink-0 rounded-[2px] bg-system-white24"
            style={{
              width: TICK_WIDTH,
              ...(isActive && resolvedGradient
                ? {
                    backgroundImage: resolvedGradient,
                    backgroundSize: `${width}px 100%`,
                    backgroundPosition: `${-i * (TICK_WIDTH + TICK_GAP)}px 0`,
                    backgroundRepeat: 'no-repeat',
                  }
                : null),
            }}
          />
        );
      })}
    </div>
  );
};
